import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';

import React, { useEffect, useState } from 'react';
import { Navigation, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { IProduct } from '@/models';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { fetchRelatedProductsBySellingCode } from '@/fetchers/products';
import { getToken } from '@/helpers/auth';
import { useRouter } from 'next/router';
import { getLangCountry } from '@/helpers/languages';
import rangeMap from '@/lib/range-map';
import Skeleton from '@/components/v2/UI/Skeleton';
import ProductCard from '../../Category/Products/ProductCard';

interface Props {
  product: IProduct;
}

const Related: React.FC<Props> = ({ product }) => {
  const { t } = useTranslation();

  const [hasScrolled, setHasScrolled] = useState(false);

  const token = getToken();
  const router = useRouter();
  const { lang, country } = getLangCountry(router.query);

  useEffect(() => {
    function handler() {
      setHasScrolled(true);
    }

    window.addEventListener('scroll', handler);

    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, []);

  const { data: products, isLoading } = useQuery({
    queryKey: ['related_products', product.selling_code],
    queryFn: async () => {
      return fetchRelatedProductsBySellingCode(product.selling_code, {
        token,
        lang,
        country
      });
    },
    enabled: hasScrolled
  });

  if (isEmpty(products)) {
    return null;
  }

  return (
    <section className="section full bg-sand-100">
      <div className="section-wrapper space-y-md">
        <div className="h3 mb-12">{t('P_PDP.RELATED.TITLE')}</div>
      </div>

      <Swiper
        wrapperTag="ul"
        slidesPerView={1.2}
        spaceBetween={16}
        modules={[Scrollbar, Navigation]}
        navigation={true}
        scrollbar={{
          hide: false
        }}
        className="px-6 md:px-10 xl:px-16 swiperScrollbar"
        breakpoints={{
          768: {
            slidesPerView: 2.2
          },
          1024: {
            slidesPerView: 3.2
          }
        }}
      >
        {isLoading
          ? rangeMap(5, idx => (
              <SwiperSlide key={idx} tag="li">
                <Skeleton key={idx} className="w-full h-60 md:h-450 xl:h-500" />
              </SwiperSlide>
            ))
          : products?.map((product, idx) => (
              <SwiperSlide key={idx} tag="li">
                <ProductCard data={product} />
              </SwiperSlide>
            ))}
      </Swiper>
    </section>
  );
};

export default Related;
